import React, { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useEffectEvent } from 'use-effect-event';
import addTestId from '../utils/addTestId';
import Button from './Button';
import * as styles from './InfiniteScrollWaypoint.module.scss';
import LoadingIndicator from './LoadingIndicator';

export type FetchMoreHandler = () => Promise<unknown> | void;

interface WaypointProps {
  onEnter: () => void;
  'data-testid'?: string;
}

const Waypoint = ({ onEnter, 'data-testid': dataTestId }: WaypointProps) => {
  const ref = useRef(null);

  const onEnterEvent = useEffectEvent(onEnter);
  useEffect(() => {
    if (!ref.current) return undefined;

    const observer = new IntersectionObserver((entries) => {
      if (entries.at(-1)?.isIntersecting) {
        onEnterEvent();
      }
    });
    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  return <div data-testid={dataTestId} className={styles.waypoint} ref={ref} />;
};

interface InfiniteScrollWaypointProps {
  fetchMore?: FetchMoreHandler | null;
  maxConsecutivePage?: number;
  prefetchDistance?: number;
}

const InfiniteScrollWaypoint = ({
  fetchMore,
  maxConsecutivePage = 10,
  prefetchDistance = 0,
}: InfiniteScrollWaypointProps) => {
  const isFetchingRef = useRef(false);
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [consecutivePageLoaded, setConsecutivePageLoaded] = useState(0);

  const handleEnter = ({
    isTriggeredManually,
  }: {
    isTriggeredManually: boolean;
  }) => {
    if (fetchMore && !isFetchingRef.current) {
      isFetchingRef.current = true;
      const promise = Promise.resolve(fetchMore());
      setConsecutivePageLoaded(isTriggeredManually ? 0 : (p) => p + 1);

      promise
        .catch(async () => {
          await new Promise((resolve) => {
            setTimeout(resolve, 2000);
          });
          return fetchMore();
        })
        .finally(() => {
          isFetchingRef.current = false;
        })
        .then(
          () => setState('idle'),
          () => setState('error'),
        );

      // We need to set flushSync here, because since automatic batching,
      // it can happen (for example in test code) that the promise either resolves immediately
      // or that the promise resolves in a setTimeout with 0 delay. In both these cases,
      // the call setState('loading') bellow will be batched with setState above.
      // However, the components logic expects that `state === 'loading'`
      // is true at least once when the promise is resolved, because it will cause Waypoint
      // to be unmounted and then remounted again when `state === 'loading'` is false again.
      // Since this behavior is specific to this component, instead of changing all test code
      // to use setTimeout with delay 1 everywhere, we decided to use flushSync here.
      flushSync(() => {
        setState('loading');
      });
    }
  };

  if (!fetchMore) {
    return null;
  }

  if (state === 'idle' && consecutivePageLoaded < maxConsecutivePage) {
    return (
      <>
        {!!prefetchDistance && (
          <div style={{ transform: `translateY(${-prefetchDistance}px)` }}>
            <Waypoint
              {...addTestId('prefetch-waypoint')}
              onEnter={() => handleEnter({ isTriggeredManually: false })}
            />
          </div>
        )}
        <Waypoint
          {...addTestId('waypoint')}
          onEnter={() => handleEnter({ isTriggeredManually: false })}
        />
      </>
    );
  }

  return (
    <div className={styles.loader}>
      {state === 'loading' && <LoadingIndicator delay={0} />}
      {(state === 'error' || state === 'idle') && (
        <Button
          block
          variant="link"
          onClick={() => {
            handleEnter({ isTriggeredManually: true });
          }}
        >
          {state === 'error' ? 'Try again' : 'Load more'}
        </Button>
      )}
    </div>
  );
};

export default InfiniteScrollWaypoint;
